import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogoMark } from "./Logo";
import { scrollToSection } from "./SmoothScroll";
import { waLink } from "../data/site";

const LINKS = [
  { label: "Home", to: "/#home", hash: "#home", testid: "main-nav-link-home" },
  { label: "Products", to: "/#products", hash: "#products", testid: "main-nav-link-products" },
  { label: "Gallery", to: "/#gallery", hash: "#gallery", testid: "main-nav-link-gallery" },
  { label: "Contact", to: "/#contact", hash: "#contact", testid: "main-nav-link-contact" },
];

const WhatsAppIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.52 3.48A11.87 11.87 0 0 0 12.04 0C5.46 0 .1 5.35.1 11.93c0 2.1.55 4.16 1.6 5.97L0 24l6.27-1.64a11.9 11.9 0 0 0 5.76 1.47h.01c6.58 0 11.93-5.35 11.93-11.93 0-3.19-1.24-6.19-3.45-8.42ZM12.04 21.8a9.9 9.9 0 0 1-5.05-1.38l-.36-.21-3.72.97 1-3.63-.24-.37a9.86 9.86 0 0 1-1.51-5.25c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.12 1.03 6.98 2.9a9.8 9.8 0 0 1 2.89 6.98c0 5.45-4.44 9.88-9.89 9.88Zm5.42-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.5 0 1.47 1.07 2.9 1.22 3.1.15.2 2.1 3.2 5.1 4.49.71.3 1.27.49 1.7.63.72.23 1.37.2 1.88.12.58-.09 1.76-.72 2-1.42.25-.7.25-1.29.18-1.42-.07-.13-.27-.2-.57-.35Z" />
  </svg>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("#home");

  const goTo = (hash) => (e) => {
    e.preventDefault();
    window.history.replaceState(null, "", hash);
    scrollToSection(hash);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = LINKS.map((l) => l.hash.slice(1));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(`#${entry.target.id}`);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-white/10 bg-obsidian/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-20 sm:px-6 lg:px-8">
        <Link to="/#home" onClick={goTo("#home")} className="group flex items-center" data-testid="main-nav-logo">
          <LogoMark className="h-6 sm:h-7" />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <Link
              key={l.hash}
              to={l.to}
              onClick={goTo(l.hash)}
              data-testid={l.testid}
              className={`nav-link text-sm ${
                activeSection === l.hash ? "active" : "text-fog"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <a
            href={waLink("Hi Ramaani Electricals, I have a question.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="main-nav-whatsapp-cta"
            className="flex items-center gap-2 rounded-full bg-wa px-5 py-2.5 text-sm font-semibold text-obsidian transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_28px_rgba(37,211,102,0.4)]"
          >
            <WhatsAppIcon size={15} />
            WhatsApp Us
          </a>
        </nav>
      </div>

      <nav
        className="flex items-center justify-between border-t border-white/5 bg-obsidian/80 px-6 py-3 backdrop-blur-xl lg:hidden"
        data-testid="mobile-section-bar"
      >
        {LINKS.map((l) => (
          <Link
            key={l.hash}
            to={l.to}
            onClick={goTo(l.hash)}
            data-testid={`mobile-bar-link-${l.hash.slice(1)}`}
            className={`nav-link text-sm font-medium ${
              activeSection === l.hash ? "active" : "text-fog"
            }`}
          >
            {l.label}
          </Link>
        ))}
      </nav>
    </header>
  );
};
